import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListInventoryPlans,
  useUpdateInventoryPlan,
  getListInventoryPlansQueryKey,
} from "@workspace/api-client-react";
import type { InventoryPlan } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { ArchivePlanDialog } from "@/components/spaces/archive-plan-dialog";

/**
 * SpacePlansInline — Phase 25 Plan 25-05.
 *
 * Renders the nested inline plans table inside an expanded /spaces row. Replaces the
 * affordance previously buried in the popup editor (deleted in Plan 25-06).
 *
 * Features:
 * - Active/Archived filter toggle (default = Active) with live counts
 * - Per-plan Switch:
 *     - Active   → Archived: opens ArchivePlanDialog confirmation (generic copy)
 *     - Archived → Active:   instant flip via PATCH (no confirmation)
 *   No new server endpoint — both flips use existing useUpdateInventoryPlan.
 * - Edit button per row navigates to /billing/plans/{plan.id} (PlanEditorPage)
 * - "+ Add Plan to this Space" navigates to /billing/plans/new?spaceId={spaceId}
 *
 * The plans list is fetched once via useListInventoryPlans({}) (no spaceId filter on
 * the server hook today) and filtered client-side by plan.spaceId === spaceId. Each
 * mounted expansion gets its own filter+dialog state — independent across spaces.
 */
export interface SpacePlansInlineProps {
  spaceId: number;
}

type FilterValue = "Active" | "Archived";

export function SpacePlansInline({ spaceId }: SpacePlansInlineProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const plansQuery = useListInventoryPlans({});
  const updatePlan = useUpdateInventoryPlan();

  const [filter, setFilter] = useState<FilterValue>("Active");
  const [archiveDialog, setArchiveDialog] = useState<{
    open: boolean;
    plan: InventoryPlan | null;
  }>({ open: false, plan: null });
  const [confirming, setConfirming] = useState(false);

  const allPlansForSpace = useMemo(() => {
    return (plansQuery.data ?? []).filter((p) => p.spaceId === spaceId);
  }, [plansQuery.data, spaceId]);

  const activeCount = useMemo(
    () => allPlansForSpace.filter((p) => p.status === "Active").length,
    [allPlansForSpace],
  );
  const archivedCount = useMemo(
    () => allPlansForSpace.filter((p) => p.status === "Archived").length,
    [allPlansForSpace],
  );

  const filteredPlans = useMemo(
    () => allPlansForSpace.filter((p) => p.status === filter),
    [allPlansForSpace, filter],
  );

  function invalidatePlans() {
    queryClient.invalidateQueries({ queryKey: getListInventoryPlansQueryKey() });
  }

  async function handleSwitchToggle(plan: InventoryPlan, nextChecked: boolean) {
    if (nextChecked) {
      // Archived → Active: instant flip, no confirmation
      try {
        await updatePlan.mutateAsync({
          id: plan.id,
          data: { status: "Active" },
        });
        toast({ title: `Activated "${plan.planName}"` });
        invalidatePlans();
      } catch {
        toast({
          title: "Could not activate plan. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      // Active → Archived: open confirmation
      setArchiveDialog({ open: true, plan });
    }
  }

  async function confirmArchive() {
    if (!archiveDialog.plan) return;
    setConfirming(true);
    try {
      await updatePlan.mutateAsync({
        id: archiveDialog.plan.id,
        data: { status: "Archived" },
      });
      toast({ title: `Archived "${archiveDialog.plan.planName}"` });
      invalidatePlans();
      setArchiveDialog({ open: false, plan: null });
    } catch {
      toast({
        title: "Could not archive plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="bg-muted/30 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={filter === "Active" ? "default" : "outline"}
            onClick={() => setFilter("Active")}
          >
            Active ({activeCount})
          </Button>
          <Button
            size="sm"
            variant={filter === "Archived" ? "default" : "outline"}
            onClick={() => setFilter("Archived")}
          >
            Archived ({archivedCount})
          </Button>
        </div>
        <Button
          size="sm"
          onClick={() => navigate(`/billing/plans/new?spaceId=${spaceId}`)}
        >
          + Add Plan to this Space
        </Button>
      </div>

      {plansQuery.isLoading ? (
        <p className="text-sm text-muted-foreground italic">Loading plans…</p>
      ) : filteredPlans.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No {filter.toLowerCase()} plans for this space.
        </p>
      ) : (
        <div className="rounded-md border bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Cadence</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Monthly ₹</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell className="font-medium">{plan.planName}</TableCell>
                  <TableCell className="text-xs">{plan.cadence}</TableCell>
                  <TableCell className="text-xs">
                    {plan.resourceType ?? "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {plan.monthlyPrice != null
                      ? `₹${Number(plan.monthlyPrice).toLocaleString("en-IN")}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={plan.status === "Active"}
                        onCheckedChange={(checked) =>
                          handleSwitchToggle(plan, checked)
                        }
                        disabled={updatePlan.isPending}
                        aria-label={
                          plan.status === "Active"
                            ? `Archive ${plan.planName}`
                            : `Activate ${plan.planName}`
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        {plan.status}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/billing/plans/${plan.id}`)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ArchivePlanDialog
        open={archiveDialog.open}
        onOpenChange={(open) =>
          setArchiveDialog((s) => ({
            open,
            plan: open ? s.plan : null,
          }))
        }
        planName={archiveDialog.plan?.planName ?? ""}
        onConfirm={confirmArchive}
        confirming={confirming}
      />
    </div>
  );
}
